import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Chip,
  Button,
  CircularProgress,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Skeleton,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { HiArrowLeft, HiPencil, HiTrash, HiPlus, HiShieldCheck, HiUsers } from "react-icons/hi";
import { useSnackbar } from "notistack";
import AdminV2Breadcrumbs from "../Breadcrumbs";
import EditOrganizationModal from "./EditOrganizationModal";
import CreateRoleModal from "./CreateRoleModal";
import EditRoleModal from "./EditRoleModal";
import DeleteRoleDialog from "./DeleteRoleDialog";
import DeleteOrganizationDialog from "./DeleteOrganizationDialog";
import AssignUserModal from "../assignments/AssignUserModal";
import RemoveAssignmentDialog from "../assignments/RemoveAssignmentDialog";
import ChangeRoleDialog from "../assignments/ChangeRoleDialog";
import {
  useAdminOrganizationDetails,
  useAdminRolesByOrg,
  useAdminDeleteRole,
  useAdminDeleteOrganization,
  useAdminDeleteAssignment,
  useAdminCreateAssignment,
} from "@/api/hooks/admin.hooks";
import type { OrganizationDetailDto, OrganizationsUserDetailDto } from "@/api/types/dtos";
import type { RoleDto } from "@/api/types/dtos/role-privilege.dto";

interface TabPanelProps {
  children?: React.ReactNode;
  value: number;
  index: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`org-detail-tabpanel-${index}`}
      aria-labelledby={`org-detail-tab-${index}`}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `org-detail-tab-${index}`,
    "aria-controls": `org-detail-tabpanel-${index}`,
  };
}

const OrganizationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [tabValue, setTabValue] = useState(0);
  const [editOrg, setEditOrg] = useState<OrganizationDetailDto | null>(null);
  const [createRoleOpen, setCreateRoleOpen] = useState(false);
  const [editRole, setEditRole] = useState<RoleDto | null>(null);
  const [deleteRole, setDeleteRole] = useState<RoleDto | null>(null);
  const [deleteOrgConfirm, setDeleteOrgConfirm] = useState(false);
  const [assignUserOpen, setAssignUserOpen] = useState(false);
  const [removeMember, setRemoveMember] = useState<{
    userId: string;
    userName: string;
    roleName: string;
    userRoleId: string;
  } | null>(null);
  const [addRoleMember, setAddRoleMember] = useState<{
    userId: string;
    userName: string;
  } | null>(null);
  const [addRoleSelected, setAddRoleSelected] = useState<RoleDto | null>(null);

  const {
    data: orgData,
    isLoading: orgLoading,
    isError: orgError,
    refetch: refetchOrg,
  } = useAdminOrganizationDetails(id || "");

  const {
    data: rolesData,
    isLoading: rolesLoading,
    isError: rolesError,
    refetch: refetchRoles,
  } = useAdminRolesByOrg(id || "");

  const deleteRoleMutation = useAdminDeleteRole();
  const deleteOrgMutation = useAdminDeleteOrganization();
  const deleteAssignmentMutation = useAdminDeleteAssignment();
  const createAssignmentMutation = useAdminCreateAssignment();

  const org = orgData?.data as OrganizationDetailDto | undefined;
  const roles: RoleDto[] = (rolesData?.data ?? []) as RoleDto[];

  const organizationsUsers: OrganizationsUserDetailDto[] = org?.organizationsUsers ?? [];

  interface MemberRow {
    userId: string;
    userName: string;
    userEmail: string;
    isActive: boolean;
    orgUserId: string;
    roles: Array<{ userRoleId: string; roleId: string; roleName: string }>;
  }

  const memberMap = new Map<string, MemberRow>();
  for (const ou of organizationsUsers) {
    const user = ou.user;
    if (!user) continue;
    memberMap.set(user.id, {
      userId: user.id,
      userName: [user.name, user.lastName].filter(Boolean).join(" ") || "—",
      userEmail: user.email || "—",
      isActive: user.isActive,
      orgUserId: ou.id,
      roles: (ou.roles ?? []).map((r) => ({
        userRoleId: r.userRoleId,
        roleId: r.roleId,
        roleName: r.roleName,
      })),
    });
  }
  const members = Array.from(memberMap.values());

  const handleDeleteRole = () => {
    if (!deleteRole) return;
    deleteRoleMutation.mutate(
      { id: deleteRole.id, organizationId: id },
      {
        onSuccess: () => {
          enqueueSnackbar("Rol eliminado exitosamente", { variant: "success" });
          setDeleteRole(null);
          refetchRoles();
        },
        onError: () => {
          enqueueSnackbar("Error al eliminar el rol", { variant: "error" });
        },
      }
    );
  };

  const handleDeleteOrg = () => {
    if (!id) return;
    deleteOrgMutation.mutate(id, {
      onSuccess: () => {
        enqueueSnackbar("Organización eliminada exitosamente", { variant: "success" });
        setDeleteOrgConfirm(false);
        navigate("/admin-v2");
      },
      onError: () => {
        enqueueSnackbar("Error al eliminar la organización", { variant: "error" });
      },
    });
  };

  if (orgLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  if (orgError || !org) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6" color="error" gutterBottom>
            {orgError ? "Error al cargar la organización" : "Organización no encontrada"}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<HiArrowLeft size={16} />}
            onClick={() => navigate("/admin-v2")}
            sx={{ mt: 2 }}
          >
            Volver a Organizaciones
          </Button>
          {orgError && (
            <Button variant="text" onClick={() => refetchOrg()} sx={{ mt: 2, ml: 1 }}>
              Reintentar
            </Button>
          )}
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <AdminV2Breadcrumbs
        items={[
          { label: "Admin V2", path: "/admin-v2" },
          { label: "Organizaciones", path: "/admin-v2" },
          { label: org.name || "Detalle" },
        ]}
      />

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Box>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              {org.name || "—"}
            </Typography>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 1, flexWrap: "wrap" }}>
              {org.idNumber && (
                <Typography variant="body2" color="text.secondary">
                  Documento: {org.idNumber}
                </Typography>
              )}
              <Chip
                label={org.isActive ? "Activo" : "Inactivo"}
                color={org.isActive ? "success" : "default"}
                size="small"
                variant="outlined"
              />
              {org.updatedAt && (
                <Typography variant="body2" color="text.secondary">
                  Actualizado: {new Date(org.updatedAt).toLocaleDateString("es-ES")}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<HiPencil size={16} />}
              onClick={() => setEditOrg(org)}
            >
              Editar
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<HiTrash size={16} />}
              onClick={() => setDeleteOrgConfirm(true)}
            >
              Eliminar
            </Button>
            <Button
              variant="outlined"
              startIcon={<HiArrowLeft size={16} />}
              onClick={() => navigate("/admin-v2")}
            >
              Volver
            </Button>
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ width: "100%" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={tabValue}
            onChange={(_, newValue) => setTabValue(newValue)}
            aria-label="Organization detail sections"
          >
            <Tab
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <HiShieldCheck size={18} />
                  <span>Roles</span>
                </Box>
              }
              {...a11yProps(0)}
            />
            <Tab
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <HiUsers size={18} />
                  <span>Miembros</span>
                </Box>
              }
              {...a11yProps(1)}
            />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Box sx={{ px: 3, pb: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6" fontWeight={600}>
                Roles de la Organización
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<HiPlus size={14} />}
                sx={{ flexShrink: 0 }}
                onClick={() => setCreateRoleOpen(true)}
              >
                Crear Rol
              </Button>
            </Box>

            {rolesError ? (
              <Paper sx={{ p: 3, textAlign: "center" }} variant="outlined">
                <Typography color="error" gutterBottom>
                  Error al cargar los roles
                </Typography>
                <Button variant="outlined" size="small" onClick={() => refetchRoles()}>
                  Reintentar
                </Button>
              </Paper>
            ) : rolesLoading ? (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Nombre</TableCell>
                      <TableCell>Descripción</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell align="center">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Array.from({ length: 3 }).map((_, i) => (
                      <TableRow key={`skeleton-${i}`}>
                        <TableCell>
                          <Skeleton />
                        </TableCell>
                        <TableCell>
                          <Skeleton width={200} />
                        </TableCell>
                        <TableCell>
                          <Skeleton width={80} />
                        </TableCell>
                        <TableCell>
                          <Skeleton width={100} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : roles.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: "center" }} variant="outlined">
                <HiShieldCheck size={40} style={{ opacity: 0.3, marginBottom: 8 }} />
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  No hay roles para esta organización
                </Typography>
                <Button
                  variant="text"
                  size="small"
                  startIcon={<HiPlus size={14} />}
                  sx={{ mt: 1 }}
                  onClick={() => setCreateRoleOpen(true)}
                >
                  Crear primer rol
                </Button>
              </Paper>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Nombre</TableCell>
                      <TableCell>Descripción</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell align="center">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {roles.map((role) => (
                      <TableRow key={role.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {role.name || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {role.description || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={role.isActive ? "Activo" : "Inactivo"}
                            color={role.isActive ? "success" : "default"}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
                            <Tooltip title="Editar rol">
                              <IconButton
                                size="small"
                                aria-label={`Editar rol ${role.name}`}
                                onClick={() => setEditRole(role)}
                              >
                                <HiPencil size={14} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar rol">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setDeleteRole(role)}
                                aria-label={`Eliminar rol ${role.name}`}
                              >
                                <HiTrash size={14} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box sx={{ px: 3, pb: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6" fontWeight={600}>
                Miembros de la Organización
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<HiPlus size={14} />}
                sx={{ flexShrink: 0 }}
                onClick={() => setAssignUserOpen(true)}
              >
                Añadir Miembro
              </Button>
            </Box>

            {orgLoading ? (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Nombre</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Rol(es)</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell align="center">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Array.from({ length: 3 }).map((_, i) => (
                      <TableRow key={`member-skeleton-${i}`}>
                        <TableCell>
                          <Skeleton />
                        </TableCell>
                        <TableCell>
                          <Skeleton width={180} />
                        </TableCell>
                        <TableCell>
                          <Skeleton width={100} />
                        </TableCell>
                        <TableCell>
                          <Skeleton width={80} />
                        </TableCell>
                        <TableCell>
                          <Skeleton width={80} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : members.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: "center" }} variant="outlined">
                <HiUsers size={40} style={{ opacity: 0.3, marginBottom: 8 }} />
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  No hay miembros en esta organización
                </Typography>
                <Button
                  variant="text"
                  size="small"
                  startIcon={<HiPlus size={14} />}
                  sx={{ mt: 1 }}
                  onClick={() => setAssignUserOpen(true)}
                >
                  Añadir primer miembro
                </Button>
              </Paper>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Nombre</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Rol(es)</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell align="center">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {members.map((member) => (
                      <TableRow key={member.userId} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {member.userName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {member.userEmail}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>
                            {member.roles.length > 0 ? (
                              member.roles.map((r) => (
                                <Chip
                                  key={r.userRoleId}
                                  label={r.roleName}
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  onDelete={() =>
                                    setRemoveMember({
                                      userId: member.userId,
                                      userName: member.userName,
                                      roleName: r.roleName,
                                      userRoleId: r.userRoleId,
                                    })
                                  }
                                />
                              ))
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                Sin rol asignado
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={member.isActive ? "Activo" : "Inactivo"}
                            color={member.isActive ? "success" : "default"}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
                            <Tooltip title="Añadir rol">
                              <IconButton
                                size="small"
                                aria-label={`Añadir rol a ${member.userName}`}
                                onClick={() =>
                                  setAddRoleMember({
                                    userId: member.userId,
                                    userName: member.userName,
                                  })
                                }
                              >
                                <HiPlus size={14} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </TabPanel>
      </Paper>

      <EditOrganizationModal
        open={!!editOrg}
        organization={editOrg}
        onClose={() => setEditOrg(null)}
        onSuccess={() => {
          setEditOrg(null);
          refetchOrg();
        }}
      />

      <CreateRoleModal
        open={createRoleOpen}
        onClose={() => setCreateRoleOpen(false)}
        onSuccess={() => {
          setCreateRoleOpen(false);
          refetchRoles();
        }}
        organizationId={id || ""}
      />

      <EditRoleModal
        open={!!editRole}
        role={editRole}
        onClose={() => setEditRole(null)}
        onSuccess={() => {
          setEditRole(null);
          refetchRoles();
        }}
      />

      <DeleteRoleDialog
        open={!!deleteRole}
        role={deleteRole}
        onConfirm={handleDeleteRole}
        onCancel={() => setDeleteRole(null)}
        loading={deleteRoleMutation.isPending}
      />

      <AssignUserModal
        open={assignUserOpen}
        onClose={() => setAssignUserOpen(false)}
        onSuccess={() => {
          setAssignUserOpen(false);
          refetchOrg();
        }}
        initialOrganizationId={id || ""}
      />

      <RemoveAssignmentDialog
        open={!!removeMember}
        userName={removeMember?.userName || ""}
        orgName={org?.name || ""}
        roleName={removeMember?.roleName || ""}
        onConfirm={() => {
          if (!removeMember || !id) return;
          deleteAssignmentMutation.mutate(
            {
              organizationId: id,
              userId: removeMember.userId,
              userRoleAssignmentId: removeMember.userRoleId,
            },
            {
              onSuccess: () => {
                enqueueSnackbar("Rol removido exitosamente", {
                  variant: "success",
                });
                setRemoveMember(null);
                refetchOrg();
              },
              onError: () => {
                enqueueSnackbar("Error al remover el rol", {
                  variant: "error",
                });
              },
            }
          );
        }}
        onCancel={() => setRemoveMember(null)}
        loading={deleteAssignmentMutation.isPending}
      />

      <ChangeRoleDialog
        open={!!addRoleMember}
        userName={addRoleMember?.userName || ""}
        orgName={org?.name || ""}
        roles={roles}
        loading={false}
        submitting={createAssignmentMutation.isPending}
        selectedRole={addRoleSelected}
        onRoleChange={setAddRoleSelected}
        onConfirm={async () => {
          if (!addRoleMember || !addRoleSelected || !id) return;
          try {
            await createAssignmentMutation.mutateAsync({
              userId: addRoleMember.userId,
              roleId: addRoleSelected.id,
              organizationId: id,
            });
            enqueueSnackbar("Rol asignado exitosamente", { variant: "success" });
            setAddRoleMember(null);
            setAddRoleSelected(null);
            refetchOrg();
          } catch {
            enqueueSnackbar("Error al asignar el rol", { variant: "error" });
          }
        }}
        onCancel={() => {
          setAddRoleMember(null);
          setAddRoleSelected(null);
        }}
      />

      <DeleteOrganizationDialog
        open={deleteOrgConfirm}
        organization={org}
        onConfirm={handleDeleteOrg}
        onCancel={() => setDeleteOrgConfirm(false)}
        loading={deleteOrgMutation.isPending}
      />
    </Container>
  );
};

export default OrganizationDetailPage;
